import { ChangeDetectionStrategy, Component, effect, inject, ViewEncapsulation } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { AngBridgeUtil } from "../../../util/ang.bridge.util";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { SeEditableLogo } from "../se.editable.logo.component";
import { Item } from "../../../pojo/Item";
import { SeEventCheckinStore } from "./se.event.checkin.store";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { LoginStore } from "../../../stores/login.store";
import { NgTemplateOutlet } from "@angular/common";
import { S25Const } from "../../../util/s25-const";

@TypeManagerDecorator("s25-ng-check-in")
@Component({
    standalone: true,
    selector: "s25-ng-check-in",
    template: `
        @if (!loginStore.loggedIn() && store.eventNotFound()) {
            <h1 class="status-heading">Event not found</h1>
            <ng-container [ngTemplateOutlet]="button" />
        } @else {
            <div class="event-header">
                @if (store.isLoading()) {
                    <h1 class="status-heading">Loading event details...</h1>
                } @else if (store.eventNotFound()) {
                    <h1 class="status-heading">Event not found</h1>
                } @else if (!!store.hasError()) {
                    <h1 class="status-heading">Error loading event details</h1>
                } @else {
                    <!-- TODO: What do we need to do to allow guests to see this? -->
                    @if (loginStore.loggedIn()) {
                        <se-editable-logo
                            [itemId]="store.eventId()"
                            [itemTypeId]="Item.Ids.Event"
                            [imageAttr]="store.eventImageUrl()"
                        ></se-editable-logo>
                    }
                    <div class="event-info">
                        <h1 class="event-title">{{ store.eventName() }}</h1>
                        <div class="event-details-grid">
                            <div class="detail-item">
                                <span class="detail-label">Date & Time</span>
                                <div class="detail-content">
                                    <s25-ng-icon
                                        [type]="'calendarDate'"
                                        [size]="20"
                                        class="detail-icon"
                                        [ariaLabel]="false"
                                    />
                                    <span class="detail-value">{{ store.date() }}</span>
                                </div>
                            </div>
                            @if (store.locations()?.length) {
                                <div class="detail-item">
                                    <span class="detail-label">Locations</span>
                                    <div class="detail-content">
                                        @for (location of store.locations(); track location) {
                                            <div>
                                                <s25-ng-icon
                                                    [type]="'locationMarker'"
                                                    [size]="20"
                                                    class="detail-icon"
                                                    [ariaLabel]="false"
                                                />
                                                <span class="detail-value">{{ location }}</span>
                                            </div>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }
            </div>

            @if (!store.eventNotFound()) {
                <div class="event-section">
                    <h2>Check In</h2>
                    <ng-container [ngTemplateOutlet]="button" />
                </div>
            }
        }

        <ng-template #button>
            <div class="check-in">
                @if (loginStore.loggedIn()) {
                    @if (store.checkedOut()) {
                        <p>Checked Out</p>
                    } @else if (store.checkedIn()) {
                        <p>Checked in</p>
                        <s25-ng-button [type]="'primary'" [onClick]="store.checkOut"> Check Out </s25-ng-button>
                    } @else if (store.canCheckIn()) {
                        <s25-ng-button [type]="'primary'" [onClick]="store.checkIn"> Check In </s25-ng-button>
                    } @else {
                        Check-in Unavailable
                    }
                } @else {
                    <s25-ng-button [type]="'primary'" [onClick]="loginStore.login"> Sign In </s25-ng-button>
                }
            </div>
        </ng-template>
    `,
    styles: `
        :host {
            padding: 1rem;
            display: block;
        }

        .status-heading {
            text-align: center;
            margin-bottom: 2rem;
        }

        .event-header {
            display: flex;
            gap: 3rem;
            align-items: flex-start;
            margin-bottom: 3rem;
            background: var(--color--surface-bg);
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid var(--color--border);
            padding: 1.5rem;

            .event-info {
                flex: 1;
                min-width: 0;
            }

            .event-title {
                font-size: 2.8rem;
                color: var(--color--text-strong);
                font-weight: 800;
                line-height: 1.2;
            }

            .event-details-grid {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            .detail-icon {
                flex-shrink: 0;
                margin-top: 0.2rem;
                color: var(--color--link-text);
            }

            .detail-content {
                display: flex;
                align-items: center;
                gap: 0.25rem;
            }

            .detail-label {
                font-size: 0.875rem;
                color: var(--color--text-muted);
                font-weight: 500;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            .detail-value {
                font-size: 1.125rem;
                color: var(--color--text-strong);
                position: relative;
                top: 2px;
            }
        }

        .event-section {
            margin-bottom: 3rem;

            & > h2 {
                color: var(--color--text-strong);
                font-size: 1.8rem;
                margin: 0 0 1.5rem 0;
                padding-bottom: 0.5rem;
                border-bottom: 2px solid var(--color--border);
            }
        }

        .check-in {
            font-size: 3rem !important;
            text-align: center;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [S25IconComponent, SeEditableLogo, S25ButtonComponent, NgTemplateOutlet],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeCheckInComponent {
    readonly loginStore = inject(LoginStore);
    readonly store = inject(SeEventCheckinStore);
    readonly orgSecStore = inject(SeOrgSecurityStore);

    constructor() {
        AngBridgeUtil.init(this);
        effect(() => this.orgSecStore.setOrgId(this.store.orgId()));
        effect(() => {
            const eventName = this.store.eventName();
            const titleCore = eventName ? `Check In — ${eventName}` : "Check In";
            document.title = `${titleCore} - ${S25Const.appName}`;
        });
    }

    protected readonly Item = Item;
}
